goog.provide('el8.model.base');

goog.require('el8.util.base');
goog.require('el8.util.string');
goog.require('el8.service.tz');

// -----------------------------
// Core functionality to support EL8 data models
// -----------------------------

EVENTS = {
    CREATED: 'created',
    UPDATED: 'updated',
    LOADED: 'loaded',
    DELETED: 'deleted',
    MERGED: 'merged',
    MOVED: 'moved',
    FILED: 'filed'
};

var PATIENT_MODEL_CLASSES = {
    PATIENT_ALLERGY: 'PatientAllergy',
    CARE_TEAM_PHYSICIAN_PARTICIPATION: 'CareTeamPhysicianParticipation',
    CARE_TEAM_PRACTICE_PARTICIPATION:   'CareTeamPracticeParticipation',
    CHECKLIST_TEMPLATE: 'ChecklistTemplate',
    CLINICAL_FORM_COLLECTION: 'ClinicalFormCollection',
    DOCUMENT_REMINDER_NOTIFICATION: 'DocumentReminderNotification',
    LAB_ORDER: 'LabOrder',
    LAB_ORDER_SET: 'LabOrderSet',
    LETTER:'Letter',
    LETTER_TEMPLATE: 'LetterTemplate',
    MED_ORDER: 'MedOrder',
    PATIENT: 'Patient',
    PATIENT_DRUG_INTOLERANCE: 'PatientDrugIntolerance',
    PATIENT_FAMILY_RELATIONSHIP: 'PatientFamilyRelationship',
    PATIENT_HISTORY_ITEM: 'PatientHistoryItem',
    PATIENT_MAINTENANCE_ITEM: 'PatientMaintenanceItem',
    PATIENT_PROBLEM: 'PatientProblem',
    PATIENT_PROVIDER_TEAM_MEMBER: 'PatientProviderTeamMember',
    PATIENT_SMOKING_STATUS: 'PatientSmokingStatus',
    PRACTICE_REPORT_TYPE: 'PracticeReportType',
    PRACTICE_SETTINGS: 'PracticeSettings',
    PRENATAL_FLOWSHEET: 'PrenatalFlowsheet',
    PRENATAL_FLOWSHEET_VISIBLE_MEASUREMENTS: 'PrenatalFlowsheetVisitMeasurements',
    RX_HISTORY_DOWNLOAD_SUMMARY: 'RxHistoryDownloadSummary',
    SERVICE_LOCATION: 'ServiceLocation',
    STAFF_GROUP: 'StaffGroup',
    VISIT_NOTE: 'VisitNote',
    VISIT_NOTE_BULLET: 'VisitNoteBullet',
    VISIT_NOTE_TYPE: 'VisitNoteType'
}

el8.model.toSoyMapFn = function(a) { return a.asSoyMap(); };

function _copy(arg) {
    if (arg instanceof Array) { return arg.simpleCopy(); }
    else if (arg instanceof Date || arg instanceof timezoneJS.Date) { return arg; } // must come before Object
    else if (arg instanceof Object) { return {}; }
    else { return arg; }
}

Function.prototype.inheritsFrom = function(_parent) {
    // _parent can either be a class or an object, in our case
    // almost always a class
    var isClass = (_parent.constructor === Function);
    this.prototype = isClass ? new _parent() : _parent;
    this.prototype.constructor = this;
    this.prototype.parent = isClass ? _parent.prototype : _parent;
    if (_parent.prototype._toSoyMap) {
        this.prototype._toSoyMap = _parent.prototype._toSoyMap.simpleCopy();
    }
    if (_parent.prototype._toMap) {
        this.prototype._toMap = _parent.prototype._toMap.simpleCopy();
    }
    return this;
};
// DO NOT use this standalone because it doesn't have a setter
// and hence cannot get populated in "populate"
Function.prototype.addGetProperty = function(prop, _default) {
    if (_default === undefined) { _default = null; }
    this.prototype['get'+prop.toUpperCamel()] = function() {
        return (this['_'+prop] === undefined) ? _copy(_default) : this['_'+prop];
    };
};
Function.prototype.addProperty = function(prop, _toMap, _default) {
    if (_default === undefined) { _default = null; }
    this.addGetProperty(prop, _default);
    this.prototype['set'+prop.toUpperCamel()] = function(val) {
        this['_'+prop] = (val === undefined) ? _copy(_default) : val;
        return this;
    };
    if (_toMap === true) {
        this.prototype._toMap.push(prop);
    }
    if (this.prototype._toSoyMap) { this.prototype._toSoyMap.push(prop); }
    return this;
};
Function.prototype.addProperties = function(propList, _toMap, _default) {
    for (var i=0; i<propList.length; i++) {
        this.addProperty(propList[i], _toMap, _default);
    }
    return this;
};
Function.prototype.addStringProperty = function(prop, _toMap) {
    // Defaults to returning '' if no value

    this.prototype['get'+prop.toUpperCamel()] = function() {
        return this['_'+prop] || '';
    };
    this.prototype['set'+prop.toUpperCamel()] = function(val) {
        this['_'+prop] = (val === undefined || val === null || val === '') ? '' : String(val);
        return this;
    };
    if (_toMap === true) {
        this.prototype._toMap.push(prop);
    }
    if (this.prototype._toSoyMap) { this.prototype._toSoyMap.push(prop); }
    return this;
};
Function.prototype.addIntProperty = function(prop, _toMap, _default) {
    if (_default === undefined) { _default = null; }
    this.addGetProperty(prop, _default);
    this.prototype['set'+prop.toUpperCamel()] = function(val) {
        this['_'+prop] = (val === undefined || val === null || val === '') ? _copy(_default) : val*1;
        return this;
    };
    if (_toMap === true) {
        this.prototype._toMap.push(prop);
    }
    if (this.prototype._toSoyMap) { this.prototype._toSoyMap.push(prop); }
    return this;
};
Function.prototype.addDecimalProperty = function(prop, _toMap, _default) {
    // We'll store Python Decimals as strings to preserve their accuracy
    // (converting to float will mean floating point math issues)
    // and offer a convenience function for casting to float
    this.addStringProperty.apply(this, arguments);
    this.prototype['get'+prop.toUpperCamel()+'Float'] = function() {
        var rawVal = this['get'+prop.toUpperCamel()]();
        if (el8.util.isNone(rawVal)) { return null; }
        return parseFloat(rawVal);
    };
    return this;
};

Function.prototype.addModelProperty = function(prop, model, _toMap, _default) {
    if (_default === undefined) { _default = null; }
    this.addGetProperty(prop, _default);
    this.prototype['set'+prop.toUpperCamel()] = function(val) {
        this['_'+prop] = val ? (val.getId ? val : model(val)) : _copy(_default);
        return this;
    };
    if (_toMap === true) {
        this.prototype._toMap.push(prop);
    }
    if (this.prototype._toSoyMap) { this.prototype._toSoyMap.push(prop); }
    return this;
};

Function.prototype.addModelSetProperty = function(prop, model, _toMap, _default) {
    if (_default === undefined) { _default = null; }
    this.addGetProperty(prop, _default);
    this.prototype['set'+prop.toUpperCamel()] = function(val) {
        this['_'+prop] = val ? val.map(function(a) {
            // Don't apply 'model' to already formed models
            return a.getId ? a : model(a);
        }) : _copy(_default);
        return this;
    };
    if (_toMap === true) {
        this.prototype._toMap.push(prop);
    }
    this.prototype['asMap'+prop.toUpperCamel()] = function() {
        var val = this['get'+prop.toUpperCamel()]();
        return val ? val.map(el8.model.toSoyMapFn) : val;
    };
    if (this.prototype._toSoyMap) { this.prototype._toSoyMap.push(prop); }
    return this;
};
Function.prototype.addObservableProperty = function(prop, _doSort, _default) {
    if (_default === undefined) { _default = null; }
    this.prototype['add'+prop.toUpperCamel()+'Observer'] = function(fn) {
        if (this['_'+prop+'Observable'] === undefined) {
            this['_'+prop+'Observable'] = el8.util.observableVal();
        }
        this['_'+prop+'Observable'].addObserver(fn);
    };
    this.addGetProperty(prop, _default);
    this.prototype['set'+prop.toUpperCamel()] = function(val) {
        if (_doSort === true && val instanceof Array) {
            val.sort(function(a, b) { return a.getId() - b.getId(); });
        }
        this['_'+prop] = (val === undefined) ? _copy(_default) : val;
        if (this['_'+prop+'Observable'] !== undefined) {
            this['_'+prop+'Observable'].notify(val);
        }
        return this;
    };
    if (this.prototype._toSoyMap) { this.prototype._toSoyMap.push(prop); }
    return this;
};
// TODO Need an "addListProperty"
Function.prototype.addBoolean = function(prop, _toMap, _default) {
    // When _default=null, field is nullable
    if (_default === undefined) { _default = false; }
    this.prototype['is'+prop.toUpperCamel()] = function() {
        return (this['_'+prop] === undefined) ? _copy(_default) : this['_'+prop];
    };
    this.prototype['set'+prop.toUpperCamel()] = function(val) {
        var newValue = (val === undefined || val === null) ? _copy(_default) : Boolean(val);
        this['_'+prop] = newValue;

        // try to sync new value to `rawData`. Normally this is not necessary
        // because any change to entity data would get saved to server, and the server
        // would then give us back the updated entity in the response - which
        // goes through `populate()` and thus updates `this.rawData` with the
        // new field value. However, with some boolean field save endpoints in particular,
        // the server does NOT return the updated entity, it just returns some
        // nonsense that we don't do anything with. this is our horrible hacky
        // attempt at making sure `this.rawData` gets updated correctly in those
        // scenarios.
        try {
            if (this.rawData) {
                var camelCasedProperty = 'is' + prop.toUpperCamel();
                if (this.rawData.hasOwnProperty(camelCasedProperty)) {
                    this.rawData[camelCasedProperty] = newValue;
                }

                var snakeCasedProperty = 'is_' + prop;
                if (this.rawData.hasOwnProperty(snakeCasedProperty)) {
                    this.rawData[snakeCasedProperty] = newValue;
                }
            }
        } catch (err) {
            console.warn('Failed to sync boolean property change to rawData:' + prop);
        }

        return this;
    };
    if (_toMap === true) {
        this.prototype._toMap.push(prop);
    }
    if (this.prototype._toSoyMap) { this.prototype._toSoyMap.push(prop); }
    return this;
};
Function.prototype.addDateTime = function(prop, _toMap, _default) {
    if (_default === undefined) { _default = null; }
    this.addGetProperty(prop, _default);
    this.prototype['set'+prop.toUpperCamel()] = function(val) {
      if (typeof val === "string") {
        // Check to confirm that there is no time attached to the date. For now, it only works for mm/dd/yyyy format.
        // In the future, we could add other formats to check against, but for now this is rarely needed.
        if (
          val.match(
            /^(0[1-9]|1[012])[- /.](0[1-9]|[12][0-9]|3[01])[- /.]\d\d\d\d$/
          )
        ) {
          var _value = el8.service.tz.toTzDate(val);
        } else {
          var _value = el8.service.tz.toTzDateTime(val);
        }
        this["_" + prop] = isNaN(_value) ? _copy(_default) : _value;
        } else {
            this['_'+prop] = val;
        }
    };
    if (_toMap === true) {
        this.prototype._toMap.push(prop);
    }
    this.prototype['asMap'+prop.toUpperCamel()] = function() {
        var val = this['get'+prop.toUpperCamel()]();
        if (val) { return el8.service.tz.asServerFormatDateTime(val); }
        return val;
    };
    if (this.prototype._toSoyMap) { this.prototype._toSoyMap.push(prop); }
    return this;
};
Function.prototype.addDate = function(prop, _toMap, _default) {
    if (_default === undefined) { _default = null; }
    this.addGetProperty(prop, _default);
    this.prototype['set'+prop.toUpperCamel()] = function(val) {
        if (typeof(val) === 'string') {
            var _value = el8.service.tz.toTzDate(val);
            this['_'+prop] = isNaN(_value) ? _copy(_default) : _value;
        } else {
            this['_'+prop] = val;
        }
    };
    if (_toMap === true) {
        this.prototype._toMap.push(prop);
    }
    this.prototype['asMap'+prop.toUpperCamel()] = function() {
        var val = this['get'+prop.toUpperCamel()]();
        if (val) { return el8.service.tz.asServerFormatDate(val); }
        return val;
    };
    if (this.prototype._toSoyMap) { this.prototype._toSoyMap.push(prop); }
    return this;
};
Function.prototype.addTime = function(prop, _toMap, _default) {
    if (_default === undefined) { _default = null; }
    this.addGetProperty(prop, _default);
    this.prototype['set'+prop.toUpperCamel()] = function(val) {
        if (typeof(val) === 'string') {
            var _value = el8.service.tz.toTzTime(val);
            this['_'+prop] = isNaN(_value) ? _copy(_default) : _value;
        } else {
            this['_'+prop] = val;
        }
    };
    if (_toMap === true) {
        this.prototype._toMap.push(prop);
    }
    this.prototype['asMap'+prop.toUpperCamel()] = function() {
        var val = this['get'+prop.toUpperCamel()]();
        if (val) { return el8.service.tz.asServerFormatTime(val); }
        return val;
    };
    if (this.prototype._toSoyMap) { this.prototype._toSoyMap.push(prop); }
    return this;
};
Function.prototype.makeDocument = function() {
    this.addDateTime('documentDate', true);
    this.addDateTime('chartFeedDate');
    this.addIntProperty('patientId');
    this.addIntProperty('sourcePracticeId');
    this.addBoolean('confidential', true);
    this.makeSignable();
    this.makeDeleteable();

    this.prototype.saveConfidentiality = function(newConfidentiality, successCb) {
        var that = this;
        $.el8_ajax({
            type: 'POST',
            url: el8.service.urls.get('documentConfidentiality', [this.getPatientId(), this.getId()]),
            data: JSON.stringify({ confidential: newConfidentiality }),
            success: function() {
                that.setConfidential(newConfidentiality);

                var entitySet = el8.ptdata.getEntitySet(that.getModelClass());
                entitySet.trigger(EVENTS.UPDATED, that);

                if (successCb) { successCb(); }
            }
        });
    };

    this.prototype._isDocument = true;

    return this;
};
Function.prototype.makePatientItem = function() {
    // PatientItems are immutable, so this is also a last-modified-date
    this.addDateTime('createDate');
    this.makeDeleteable();
};
Function.prototype.makeSignable = function() {
    this.addDateTime('signDate');
    this.addIntProperty('signUserId');
    this.addIntProperty('signUserBehalfId');
    this.addProperty('signUser');
    this.addProperty('signUserBehalf');
    this.prototype.isSignedOff = function() {
        return this.getSignDate() ? true : false;
    };
    this.prototype.getSignUserName = function() {
        var signUser;
        // Try to get the expanded user object first, then use the id to get it.
        if (typeof this.getSignUser === 'function') {
            signUser = el8.data.referencedUsers.create(this.getSignUser());
        }
        if (!signUser) {
            signUser = el8.data.referencedUsers.getById(this.getSignUserId());
        }
        return (signUser) ? signUser.getFullName() : '';
    };
    this.prototype.getSignUserType = function () {
        var signUser;
        // Try to get the expanded user object first, then use the id to get it.
        if (typeof this.getSignUser === 'function') {
            signUser = el8.data.referencedUsers.create(this.getSignUser());
        }
        if (!signUser) {
            signUser = el8.data.referencedUsers.getById(this.getSignUserId());
        }
        return (signUser) ? signUser.getStaffType() : '';
    };
    this.prototype.getSignUserBehalfName = function() {
        var signUserBehalf;
        // Try to get the expanded user object first, then use the id to get it.
        if (typeof this.getSignUserBehalf === 'function') {
            signUserBehalf = el8.data.referencedUsers.create(this.getSignUserBehalf());
        }
        if (!signUserBehalf) {
            signUserBehalf = el8.data.referencedUsers.getById(this.getSignUserBehalfId());
        }
        return (signUserBehalf) ? signUserBehalf.getFullName() : '';
    };
    this.prototype.getSignUserBehalfType = function () {
        var signUserBehalf;
        // Try to get the expanded user object first, then use the id to get it.
        if (typeof this.getSignUserBehalf === 'function') {
            signUserBehalf = el8.data.referencedUsers.create(this.getSignUserBehalf());
        }
        if (!signUserBehalf) {
            signUserBehalf = el8.data.referencedUsers.getById(this.getSignUserBehalfId());
        }
        return (signUserBehalf) ? signUserBehalf.getStaffType() : '';
    };
    return this;
};
Function.prototype.makeDeleteable = function() {
    this.addDateTime('deleteDate');
    this.addIntProperty('deleteUserId');
    this.prototype.isDeleted = function() {
        return this.getDeleteDate() ? true : false;
    };
    return this;
};
Function.prototype.makeHoldable = function() {
    this.addBoolean('onHold', true);
    return this;
};
Function.prototype.makeRecorded = function() {
    this.addIntProperty('userId', true);
    this.addDateTime('recordDate', true); // TODO no longer defaulting to "now"
    return this;
};
Function.prototype.makeSortable = function() {
    this.addIntProperty('rank');
};
Function.prototype.makeContactable = function() {
    this.addStringProperty('phone', true);
    this.addStringProperty('fax', true);
    this.addStringProperty('email', true);
    this.addAddressProperties();
    return this;
};
Function.prototype.addAddressProperties = function() {
    this.addStringProperty('address', true);
    this.addStringProperty('suite');
    this.addStringProperty('city', true);
    this.addStringProperty('state', true);
    this.addStringProperty('zip', true);
    this.prototype.getFullAddress = function() {
        var str = this.getAddress();
        if (str) { str += ', ' + this.getCityStateZip(); }
        else { str = this.getCityStateZip(); }
        return str;
    };
    this.prototype.getAddressLine = function() {
        return (this.getAddress() + (this.getSuite() ? ', '+this.getSuite(): ''));
    };
    this.prototype.getCityStateZip = function() {
        if (!this.getCity() && !this.getState() && !this.getZip()) { return ''; }
        return (this.getCity()+', '+this.getState()+' '+this.getZip());
    };
    this.prototype.getCityState = function() {
        if (!this.getCity() && !this.getState()) { return ''; }
        return this.getCity()+', '+this.getState();
    };
    return this;
};
Function.prototype.addIcd9s = function() {
    this.addProperty('icd9Ids', true, []);
    this.addProperty('icd9Codes', false, []);

    this.prototype.getIcd9CodeObjs = function() {
        // the _.compact makes sure that if we have a cross-practice ICD9 id
        // for some reason, we won't stick a "null" in the list
        // (which can cause things down the line to explode)
        return _.compact(this.getIcd9Ids().map(function(id) {
            return el8.data.icd9Codes.getById(id);
        }));
    };
};
Function.prototype.addIcd10s = function() {
    this.addProperty('icd10Ids', true, []);
    this.addProperty('icd10Codes', false, []);
    this.addProperty('icd10LastAddressedDates', true, []);

    this.prototype.getIcd10CodeObjs = function() {
        return _.compact(this.getIcd10Ids().map(function(id) {
            return el8.data.icd10Codes.getById(id);
        }));
    };
};
Function.prototype.addImos = function() {
    this.addProperty('imoIds', true, []);

    this.prototype.getImoCodeObjs = function(includeDeletedImos) {
        return _.compact(this.getImoIds().map(function(id) {
            // We need to always call `getByIdIncludingDeleted` regardless of whether
            // `includeDeletedImos` is true, because this way we'll understand if something
            // is already deleted (i.e. we'll check the deleted cache) and if so, NOT
            // try to make a network request to fetch it.
            //
            // Previously, this code chose whether to call `getById` or `getByIdIncludingDeleted`
            // based on the value of `includeDeletedImos`, but calling `getById` for IMOs that we
            // should have already known were deleted would cause extremely poor performance problems
            // when `getImoCodeObjs` was called many times in a short period of time. It was leading
            // to repeated synchronous fetches of the same IMO codes we already knew were deleted,
            // bypassing any sort of caching mechanism to prevent duplicate fetches, and this would
            // end up freezing the UI for a total of up to 2+ minutes at a time in extreme cases.
            // 
            // For more info, see this commit's message, or:
            // https://elationhealth.atlassian.net/browse/RM-67702?focusedCommentId=287287
            var imo = el8.data.imos.getByIdIncludingDeleted(id);
            if (imo && imo.isDeleted()) {
                return includeDeletedImos ? imo : null;
            }

            return imo;
        }));
    };
};
Function.prototype.addDocTags = function() {
    // intended to be invoked on a Document
    this.addProperty('docTagIds', false, []);

    this.prototype.getDocTags = function() {
        return _.compact(this.getDocTagIds().map(function(id) {
            return el8.data.docTags.getById(id);
        }));
    };
    this.prototype.getDocTagMaps = function() {
        return this.getDocTags().map(el8.model.toSoyMapFn);
    };
    this.prototype.saveDocTags = function(newTagIds, opts) {
        var doc = this; // `this` should be a Document
        $.el8_ajax({
            type: 'POST',
            url: el8.service.urls.get('applyDocumentTagsToDocument', [doc.getPatientId(), doc.getId()]),
            data: JSON.stringify({ doc_tags: newTagIds }),
            success: function(data) {
                el8.data.docTags.create(data);
                doc.setDocTagIds(_.pluck(data, 'id'));

                if (opts.successCb) { opts.successCb(data); }
            },
            error: opts.errorCb
        });
    };
};
Function.prototype.addEvents = function() {
    this.prototype.bind = function(ev, callback, context) {
        /* Inspired by Backbone.Events */
        var calls = this._callbacks || (this._callbacks = {});
        var list = calls[ev] || (calls[ev] = []);
        list.push([callback, context]);
        return this;
    };
    this.prototype.unbind = function(ev, callback) {
        if (!ev) { this._callbacks = {}; }
        else if (this._callbacks) {
            var calls = this._callbacks;
            if (!callback) {
                calls[ev] = [];
            } else {
                var list = calls[ev];
                if (!list) { return this; }
                for (var i=0; i<list.length; i++) {
                    if (list[i] && callback === list[i][0]) {
                        list[i] = null;
                        break;
                    }
                }
            }
        }
        return this;
    };
    this.prototype.trigger = function(eventName) {
        var ev, list, callback, args;
        var calls = this._callbacks;
        var both = 2;
        if (!calls) { return this; }
        while (both--) {
            ev = both ? eventName : 'all';
            list = calls[ev];
            if (list) {
                for (var i=0; i<list.length; i++) {
                    if (!(callback = list[i])) {
                        list.splice(i, 1); i--;
                    } else {
                        args = both ? Array.prototype.slice.call(arguments, 1) : arguments;
                        callback[0].apply(callback[1] || this, args);
                    }
                }
            }
        }
        return this;
    };
};

// -----------------------------
// Core ENTITY model
// -----------------------------
el8_Entity = function() {};
el8_Entity.prototype._toMap = [];
el8_Entity.prototype._toSoyMap = [];

el8_Entity.addProperty('id', true);
el8_Entity.addProperty('modelClass');
el8_Entity.prototype.is = function(mClass) { return this.getModelClass().cmp(mClass); };
/**
 * This function produces the representation of the model that we'll post to
 * the server. Any new usages should set `underscore` (as in underscores instead
 * of camel in JSON) to true and prefixBools should probably be false
 * (you can leave prefixBools alone if your server-side bools all start with `is_`)
 */
el8_Entity.prototype.asMap = function(underscore, prefixBools) {
    var r = {};
    var toMap = this.constructor.prototype._toMap;
    var translate = function (v) {
        if (v instanceof el8_Entity) {
            return v.asMap(underscore, prefixBools);
        } else {
            return v;
        }
    };

    // Default true to prepend 'is' to keys sent to the server (for backwards compatibility).
    // We eventually want to get rid of this behavior entirely
    prefixBools = (prefixBools !== false);

    for(var i=0; i<toMap.length; i++) {
        var formatted_prop = toMap[i].toUpperCamel();
        var map_prop = (underscore === true) ?
                        toMap[i].fromCamelToUnderscore() : toMap[i];
        // Is this a regular property?
        if (this['get'+formatted_prop]) {

            var val = this['asMap'+formatted_prop] ?
                this['asMap'+formatted_prop]() : this['get'+formatted_prop]();

            if (val instanceof Array) {
                r[map_prop] = val.map(translate);
            } else {
                r[map_prop] = translate(val);
            }

        // Else, is this a Boolean property?
        } else if (this['is'+formatted_prop]) {
            var map_is_prop;
            if (prefixBools) {
                map_is_prop = (underscore === true) ? 'is_'+map_prop : 'is'+formatted_prop;
            } else {
                map_is_prop = (underscore === true) ? map_prop : formatted_prop;
            }
            r[map_is_prop] = this['is'+formatted_prop]();
        }
    }
    return r;
};
/**
 * A "soy map" is a client-side representation of a model, intended primarily for use
 * in templates (named after Soy Templates). Ideally, its contents are as close as
 * possible to the contents of encode_json() for the model (server-side), because that
 * makes it much easier to use the templates server-side. The main exception is that
 * the server will often send down just an ID for a referenced object (e.g. a User
 * referenced by a Document) and the soy map can sub-in the full object (because templates
 * don't know how to turn an ID into an object/its corresponding soy map and the server
 * can easily do that operation too; but display logic directly in a soy map, e.g., will
 * cause issues when the server tries to use the same template)
 *
 * `propertiesToExclude` allow you to avoid encoding expensive objects that you know
 * will not be used by your template(s). Note that it was added relatively late and
 * many models may not correctly it in from their overriden version of asSoyMap.
 * So check before using it in any new model.
 */
el8_Entity.prototype.asSoyMap = function(propertiesToExclude) {
    var r = {};
    var toSoyMap = this.constructor.prototype._toSoyMap;
    var translate = function (v) {
        if (v instanceof el8_Entity) {
            return v.asMap();
        } else {
            return v;
        }
    };

    if (propertiesToExclude && _.isArray(propertiesToExclude) && propertiesToExclude.length) {
        toSoyMap = _.difference(toSoyMap, propertiesToExclude);
    }

    for(var i=0; i<toSoyMap.length; i++) {
        var formatted_prop = toSoyMap[i].toUpperCamel();
        var map_prop = toSoyMap[i];

        // Is this a regular property?
        if (this['get'+formatted_prop]) {

            var val = this['asMap'+formatted_prop] ?
                this['asMap'+formatted_prop]() : this['get'+formatted_prop]();

            if (val instanceof Array) {
                r[map_prop] = val.map(translate);
            } else {
                r[map_prop] = translate(val);
            }

        // Else, is this a Boolean property?
        } else if (this['is'+formatted_prop]) {
            var map_is_prop = 'is'+formatted_prop;
            r[map_is_prop] = this['is'+formatted_prop]();
        }
    }
    return r;
};
el8_Entity.prototype.populate = function(spec, syncToRedux, rawData) {
    spec = spec || {};
    var that = this;
    for (var prop in spec) {
        if (spec.hasOwnProperty(prop)) {
            var formatted_prop = prop.fromUnderscoreToCamel();
            if (that['set'+formatted_prop.toUpperCamel()]) {
                that['set'+formatted_prop.toUpperCamel()](spec[prop]);

            // Some Booleans has an "is" pre-pended that we will ignore
            } else {
                var is = (formatted_prop.toLowerCase().indexOf('is') === 0);
                if (is && that['set'+formatted_prop.substring(2).toUpperCamel()]) {
                    that['set'+formatted_prop.substring(2).toUpperCamel()](spec[prop]);
                }
            }
        }
    }
    // `rawData` can be manually overridden to something other than `spec`, which
    // is important for use cases where `spec` gets mutated (which legacy code LOVES
    // to do). in those situations, make sure you pass in something like a deep clone
    // of `spec` for the `rawData` arg, to avoid corruption of `rawData`.
    //
    // default value of `spec` may be wrong if spec is an incomplete object, but not
    // sure if that use case exists.
    this.rawData = rawData || spec;

    // Check that spec has a model class before attempting to sync to Redux, since
    // non-model-class things have no need to live in Redux. Incidentally, this
    // also checks that spec is a raw data object and not an el8Entity (because
    // model_class is still snake cased and hasn't been camelCased).
    if (spec.model_class && window.el8React && el8React.legacyToReduxSynchronizer && syncToRedux !== false) {
        el8React.legacyToReduxSynchronizer.updateEntity(spec);
    }

    return this;
};
el8_Entity.prototype.getChartFeedDate = function() {
    // Basic functionality; gets over-written for Documents in makeDocument()
    var that = this;
    if (that.getDocumentDate) { return that.getDocumentDate(); }
    if (that.getCreateDate) { return that.getCreateDate(); }
    return null;
};

el8_Entity.prototype.getFeedItemId = function(val) {
    return 'feedItem_'+this.getModelClass()+'_'+this.getId();
};


// --------------------------------
// Core ENTITY SET model
// --------------------------------

el8_EntitySet = function(spec) {

    spec = spec || {};

    this._all = [];
    this._mapped = {};
    this._mappedById = {}; // Used only if _key is not equal to 'id'

    this._unsaved = [];
    this._deleted = [];

    this._key = spec.key || 'id'; // default is to map by id
    this._model = spec.model || function(){};
    this._getter = 'get'+this._key.capitalize();

    // default is to sort by id
    this._sortFn = spec.sortFn || function(a, b) { return b.getId()-a.getId(); };

    // these fn's should take two to four arguments: an item, successCb, errorCb, and an opts dict
    this._saveFn = spec.saveFn || null;
    this._deleteFn = spec.deleteFn || null;
    this._mergeFn = spec.mergeFn || null;

    this._urlFn = spec.urlFn || null;
    this._urlDataFn = spec.urlDataFn || null;

    this._defaultFetch = (spec.defaultFetch !== false); // defaults to True - fetch

    // Can also specify a urlIdFn, which takes id as arg, regardless of _key
    // Used in getById fn
    this._urlIdFn = spec.urlIdFn || null;

    // URL this loads all items
    this._urlLoad = spec.urlLoad || null;

    // URL to sort items by submitted a sorted list of IDS
    this._urlSort = spec.urlSort || null;

    // Custom handler for callbacks on load
    this._loadFn = spec.loadFn || null;

    this._observable = (spec.observable !== false) ? el8.util.observableVal() : null; // default true

    this.addObserver = this._observable ? this._observable.addObserver : function(){};
    if (spec.observers && this._observable) {
        for (var i=0; i<spec.observers.length; i++) {
            this.addObserver(spec.observers[i]);
        }
    }
    this.addItemObserver = this._observable ? this._observable.addItemObserver : function(){};
    this.notify = this._observable ? this._observable.notify : function() {};
    this.itemNotify = this._observable ? this._observable.itemNotify : function() {};
    this.addRealtimeObserver = this._observable ? this._observable.addRealtimeObserver : function() {};

    if (spec.items && spec.items.length > 0) {
        this.load(spec.items);
    }

    return this;
};

/** 2012.01.13 Started implementing this for all the meds sets **/
/** 2023.03.23 Started implementing this for all workspaces sets **/
el8_EntitySet.addEvents();

el8_EntitySet.addBoolean('loaded');
el8_EntitySet.method('addToMapped', function(item) {
    if (!this._key.cmp('id') && item.getId && item.getId() ) {
        this._mappedById[ item.getId() ] = item;

        // If we have a key that is not 'id' but we do have an ID property,
        // it is still unacceptable to have duplicate entries with the same ID.
        // So we'll delete any existing entries with item's ID here.
        for (var existingKey in this._mapped) {
            if (this._mapped.hasOwnProperty(existingKey)) {
                var existingItem = this._mapped[existingKey];
                if (existingItem.getId() === item.getId()) {
                    delete this._mapped[existingKey];
                }
            }
        }
    }
    this._mapped[ item[this._getter]() ] = item;

    var modelClass = item.getModelClass && item.getModelClass();
    if (modelClass && item.rawData && window.el8React && el8React.legacyToReduxSynchronizer) {
      el8React.legacyToReduxSynchronizer.updateEntity(item.rawData);
    }
});
el8_EntitySet.method('removeFromMapped', function(item) {
    if (!this._key.cmp('id') && item.getId ) {
        delete this._mappedById[ item.getId() ];
    }
    delete this._mapped[ item[this._getter]() ];

    var modelClass = item.getModelClass && item.getModelClass();
    if (modelClass && item.rawData && window.el8React && el8React.legacyToReduxSynchronizer) {
      el8React.legacyToReduxSynchronizer.removeEntity(item.rawData);
    }
});
el8_EntitySet.method('realtimeNotify', function(var_args) {
    if (this._observable) {
        this._observable.realtimeNotify.apply(this._observable, arguments);
    }
});
el8_EntitySet.method('notifyObserver', function(noNotify, item) {
    if (this._observable && !noNotify) { this._observable.notify(item); }
});
el8_EntitySet.method('all', function() {
    return this._all;
});
el8_EntitySet.method('allAsSoyMaps', function() {
    return this.all().map(function(a) {
        return (a.asSoyMap) ? a.asSoyMap() : {};
    });
});
el8_EntitySet.method('sortedAsSoyMaps', function() {
    return this.sorted().map(el8.model.toSoyMapFn);
});
el8_EntitySet.method('add', function(item, noNotify) {

    // Check for deleted items
    if (item.isDeleted && item.isDeleted()) {
        this.addToDeleted(item);
        // No need to do any notifying
        return;
    }

    // Else, if item has key 'getter' method
    if (item[this._getter]) {
        // if there the key is null (e.g. no id), assume unsaved item
        if (!item[this._getter]()) {
            this.addToUnsaved(item);
        } else {
            this.addToMapped(item);
            this.rebuild();
        }
    }
    this.notifyObserver(noNotify);
    if (!noNotify) { this.trigger(EVENTS.CREATED, item); }

});
el8_EntitySet.method('addOrUpdate', function(item, noNotify) {
    try {
        if (!this._mapped[ item[this._getter]() ]) {  // includes case that getter() returns null
            this.add(item, noNotify);
        } else {
            this.addToMapped(item);
            this.rebuild();
            this.notifyObserver(noNotify);
            if (!noNotify) { this.trigger(EVENTS.UPDATED, item); }
        }
    } catch(err) {
        var msg = 'Uh-oh, addOrUpdate had an error (' +
            (item.getModelClass && item.getModelClass()) + ' ' + (item.getId && item.getId()) + ')';
        msg += '\n' + err;
        if (err.stack) {
            msg += '\n\n' + err.stack;
        }
        UIM && UIM.logError(msg);
    }
});
el8_EntitySet.method('addToUnsaved', function(item) {
    for (var i=0; i<this._unsaved.length; i++) {
        if (this._unsaved[i].getId() === item.getId()) {
            this._unsaved.splice(i, 1, item);
            return;
        }
    }
    this._unsaved.push(item);
});
el8_EntitySet.method('addToDeleted', function(item) {
    for (var i=0; i<this._deleted.length; i++) {
        if (this._deleted[i].getId() === item.getId()) {
            this._deleted.splice(i, 1, item);
            return;
        }
    }
    this._deleted.push(item);
});
el8_EntitySet.method('removeFromDeleted', function(itemOrId) {
    var oldLength = this._deleted.length;
    if (oldLength === 0) { return false; }

    if (typeof(itemOrId) !== "number" ){
        itemOrId = itemOrId.getId();
    }

    this._deleted = this._deleted.filter(function(item) {
        return item.getId() !== itemOrId;
    });

    return (this._deleted.length < oldLength)
});
el8_EntitySet.method('updateItemId', function(item, newId) {
    if (item.getId() !== (newId*1)) {
        // If able to sync with Redux for this item, remove the existing item from
        // Redux. It's expected that a `populate` call will come soon after the
        // `updateItemId` call, which will provide the updated item with the new
        // ID to Redux.
        var modelClass = item.getModelClass && item.getModelClass();
        var itemId = item.getId && item.getId();
        if (modelClass && itemId && window.el8React && el8React.legacyToReduxSynchronizer) {
            var itemArg = {
                id: itemId,
                model_class: modelClass
            }
            el8React.legacyToReduxSynchronizer.removeEntity(itemArg);
        }

        var oldId = item.getId();
        item.setId(newId);
        this._mapped[newId] = item;
        delete this._mapped[oldId];
        if (this._mappedById[oldId]) {
            this._mappedById[newId] = item;
            delete this._mappedById[oldId];
        }
        return true;
    }
    return false;
});
el8_EntitySet.method('create', function(data, noNotify) {
    if (!data) { return null; }
    if (data instanceof Array) {
        var created = [];
        for (var i=0; i<data.length; i++) {
            var m = this.create(data[i], true);
            created.push(m);
        }
        this.notifyObserver(noNotify);
        if (!noNotify) { this.trigger(EVENTS.CREATED, created); }
        return created;
    } else {
        var m = this._model(data);
        this.addOrUpdate(m, noNotify);
        return m;
    }
});
el8_EntitySet.method('recreate', function(data, noNotify) {
    if (!this._model) { return null; }
    this.reset();
    this.create(data, noNotify);
});
el8_EntitySet.method('reset', function() {
    this._all = [];
    this._mapped = {};
    this._mappedById = {};
    this._unsaved = [];
    this._deleted = [];
    return this;
});
el8_EntitySet.method('softLoad', function(arg, async, noNotify, successCbFn, options) {
    if (!this.isLoaded()) {
        this.load.apply(this, arguments);
        return true;
    } else {
        successCbFn = successCbFn || (jQuery.isFunction(arg) ? arg : null);
        if (successCbFn) { successCbFn(); }
        if (!noNotify) { this.trigger(EVENTS.LOADED); }
        if (options && options.onSuccess) { options.onSuccess(); }
        return false;
    }
});
el8_EntitySet.method('debouncedLoad', function(arg, async, noNotify, successCbFn, options) {
    /* This function exists to avoid repeatedly reloading the same entity set.
     * It is useful if a string of realtime updates might each trigger a reload,
     * so we can wait until the last message before reloading */
    var that = this;
    var DEBOUNCE_RATE = 100; // wait 100ms before loading
    if (!this._debouncedLoadFn) {
        this._debouncedLoadFn = _.debounce(function() { return that.load.apply(that, arguments); }, DEBOUNCE_RATE);
    }
    return this._debouncedLoadFn();
});

/**
 * Note: by default, async is not recommended on a populated store because this function
 * will first empty out the store THEN begin the async call meaning that for the duration
 * of the network request any code using the store will not find data. Also, if the network
 * request suffers an error the store will remain empty (even if it wasn't empty before
 * calling this load function).
 *
 * To avoid these issues on an async load, pass `options.clearStore = false`. With that
 * option the store is only cleared right before it gets re-populated from a successful
 * response, so consumers of the store continue to see the existing data while the request
 * is in flight and the existing data is preserved on error. `loadAsync` already passes
 * `clearStore: false` for you.
 */
el8_EntitySet.method('load', function(arg, async, noNotify, successCbFn, options) {
    // "arg" can be an array or a function
    var that = this;

    // We need a separate ACTUAL success callback because `successCbFn` is not a
    // proper success callback - if you pass in `successCbFn`, it stops this load
    // function's internal success handling logic from running (see further below
    // where `successCbFn` is used).
    var actualSuccessCallback = options && options.onSuccess;
    var errorCallback = options && options.onError;
    // Defaults to true for backwards compatibility. When false, the store is
    // left untouched when the load kicks off, and only gets cleared after a
    // successful response (right before the fresh data is populated). See the
    // method's doc comment above for the motivation.
    var clearStore = !(options && options.clearStore === false);
    var clearStoreData = function() {
      that._all = [];
      that._mapped = {};
      that._mappedById = {};
    };

    // Attempt to identify model class for this entity set before clearing data;
    // model class will be used further down in the urlLoad success callback when
    // syncing updates to Redux.
    var modelClass;
    if (that._all.length > 0) {
      modelClass = that._all[0]._modelClass;
    }

    if (clearStore) {
      clearStoreData();
    }
    var _callbackFn = arg && jQuery.isFunction(arg) ? arg : null;
    // If arg given that is an array
    if (arg && arg instanceof Array) {
        // If the clear was deferred so the store stayed populated, do
        // it now - right before repopulating - so the load is still a
        // full replacement rather than a merge into the old data.
        // Mirrors the same logic in the AJAX path below.
        if (!clearStore) {
          clearStoreData();
        }
        if (arg.length > 0) {
            for (var i=0; i<arg.length; i++) {
                that.addOrUpdate(arg[i], true);
            }
            that.notifyObserver(noNotify);
            if (!noNotify) { that.trigger(EVENTS.LOADED); }
        }
        that.setLoaded(true);
        if (actualSuccessCallback) {
            actualSuccessCallback();
        }
        return this;
    }
    // Else, load from URL if have a url
    if (that._urlLoad) {
        var _noNotify = (noNotify === true); // defaults to false
        var _async = (async === true); // defaults to false
        $.el8_ajax({
            type: "GET",
            url: jQuery.isFunction(that._urlLoad) ? that._urlLoad() : that._urlLoad,
            async: _async,
            //dataType: "json",
            success: function(results) {
                try {
                    var data = results
                    if (results.data && results.status === 'success') {
                      // If response has both a data and status attribute, it's following
                      // Django Rest Framework conventions so the actual data is under
                      // the `data` key
                      data = results.data
                    }
    
                    var canSyncToRedux = modelClass && window.el8React && el8React.legacyToReduxSynchronizer;
                    if (canSyncToRedux) {
                      el8React.legacyToReduxSynchronizer.startLoad(modelClass);
                    }

                    // If the clear was deferred so the store stayed populated
                    // during the request, do it now - right before repopulating
                    // - so the load is still a full replacement rather than a
                    // merge into the old data.
                    if (!clearStore) {
                      clearStoreData();
                    }

                    if (successCbFn) {
                        /* Allow option of over-riding create call,
                         * for example, if resp is not structured in
                         * standard way
                         */
                        successCbFn(data);
                    } else {
                        if (that._loadFn) {
                            that._loadFn(data);
                        } else {
                            that.create(data, true);
                        }
                    }
                    that.setLoaded(true);
                    if (_callbackFn) { _callbackFn(); }
                    that.notifyObserver(_noNotify);
                    if (!noNotify) { that.trigger(EVENTS.LOADED); }
    
                    if (canSyncToRedux) {
                      el8React.legacyToReduxSynchronizer.endLoad(modelClass);
                    }
                } finally {
                    // Use finally block to ensure callback is always called
                    // even if there's an error in the other success handling
                    // code above. This is particularly important to ensure
                    // `loadAsync` does not fail to resolve its promise in these
                    // cases.
                    if (actualSuccessCallback) {
                      actualSuccessCallback(results);
                    }
                }
            },
            error: function() {
                if (errorCallback) {
                  errorCallback();
                }
            }
        });
        return this;
    } else {
        if (_callbackFn) { _callbackFn(); }
    }
    if (actualSuccessCallback) {
        actualSuccessCallback();
    }
    return this;
});
/**
 * An async version of `load` that returns a `Promise`.
 *
 * Unlike the default `load` behavior, `loadAsync` does NOT clear the store before
 * kicking off the network request. The store is only cleared right before it gets
 * re-populated from a successful response, so consumers of the store continue to see
 * the existing data while the request is in flight and the existing data is preserved
 * if the request errors out.
 *
 * @param {Object} [options] - The options for the `load` function.
 * @param {Function} [options.loadData] - A custom data loading function to override
 * `load`'s default logic for loading data from the response into the legacy data store.
 * This is the same thing as `load`'s `successCbFn` parameter, but with a more accurate name.
 * @param {Boolean} [options.noNotify] - If true, `EVENTS.LOADED` will not be triggered
 * after the load is complete. This is the same thing as `load`'s `noNotify` parameter.
 * @param {Function} [options.onSuccess] - The callback to call when the load is successful.
 * Note that the callback will only receive the data from the response as an argument if
 * a request was actually made to the server, and while rare, `load` has some code paths that
 * "succeed" without doing so. So make sure you check that there's actually something in
 * that argument before trying to use it. 
 * @param {Function} [options.onError] - The callback to call when the load fails.
 * @returns {Promise} A promise that resolves when the load is successful and rejects when it fails.
 */
el8_EntitySet.method('loadAsync', function (options) {
    var that = this;

    options = options || {};
    var loadData = options.loadData;
    var noNotify = options.noNotify;
    var onSuccess = options.onSuccess;
    var onError = options.onError;

    return new window.Promise(function (resolve, reject) {
        try {
            that.load(undefined, true, noNotify, loadData, {
                // Don't wipe the store up front - that's the whole point of
                // using `loadAsync` over the raw `load(undefined, true, ...)`
                // call. Without this, the store would be empty for the
                // duration of the network request (and stay empty on error).
                clearStore: false,
                onSuccess: function(maybeData) {
                    resolve();
                    if (onSuccess) {
                        onSuccess(maybeData);
                    }
                },
                onError: function() {
                    reject();
                    if (onError) {
                        onError();
                    }
                }
            });
        } catch (error) {
            console.error(error);
            reject();
        }
    });
});
el8_EntitySet.method('getByKey', function(key, _noFetch) {
    var that = this;
    var noFetch = (_noFetch === undefined) ? !that._defaultFetch : _noFetch;
    if (!key) { return null; }
    if (that._mapped[key]) {
        return that._mapped[key];
    } else {
        if(noFetch) { return null; }
        return that._urlFn ? (function() {
            var m = null;
            var _data = that._urlDataFn ? that._urlDataFn(key) : null;
            $.el8_ajax({
                url: jQuery.isFunction(that._urlFn) ? that._urlFn(key) : that._urlFn,
                data: _data,
                async: false,
                success: function (data, textStatus, xhr) {
                    m = that.create(data);
                }
            });
            return m;
        })() : null;
    }
});
el8_EntitySet.method('getById', function(id, _noFetch) {
    var that = this;
    var noFetch = (_noFetch === undefined) ? !that._defaultFetch : _noFetch;
    if (!id) { return null; }
    if (that._key.cmp('id')) {
        return that.getByKey(id, noFetch);
    } else {
        if (that._mappedById[id]) {
            return that._mappedById[id];
        } else {
            if (noFetch) { return null; }
            return that._urlIdFn ? (function() {
                var m = null;
                $.el8_ajax({
                    url: jQuery.isFunction(that._urlIdFn) ? that._urlIdFn(id) : that._urlIdFn,
                    async: false,
                    success: function (data, textStatus, xhr) {
                        m = that.create(data);
                    }
                });
                return m;
            })() : null;
        }
    }
});

el8_EntitySet.method('updateFromReact', function(spec) {
    var entity = this.getById(spec.id, true);
    if (entity) {
        entity.populate(spec, false);
        this.trigger('updated', entity);
    }
});

el8_EntitySet.method('getByIdIncludingDeleted', function(id, _noFetch) {
    // this will just check the local _deleted cache
    var deletedItem = this.getDeletedById(id);
    if (deletedItem) { return deletedItem; }

    /* If we didn't find it, we'll turn to a normal getById.
     * getById would not return a deleted object from the local cache,
     * but if the GET view returns deleted items, it will return
     * deleted items newly fetched from the server */
     return this.getById(id, _noFetch);
});
el8_EntitySet.method('getByName', function(name, nameType) {
    // Custom methods for model set
    var that = this;
    if (!name) { return null; }
    var _nameType = nameType || 'name';
    for (var i=0; i<that._all.length; i++) {
        if (el8.util.string.strNameEqual(name, that._all[i]['get'+_nameType.capitalize()](), true)) {
            return that._all[i];
        }
    }
    var m = null;
    if (that._urlNameFn) {
        // If we can't find it, fetch from server
        $.el8_ajax({
            url: jQuery.isFunction(that._urlNameFn) ? that._urlNameFn(name) : that._urlNameFn,
            data: {q:name,exact:true},
            async: false,
            //dataType: 'json',
            success: function(data, textStatus, xhr) {
                // If there are multiple responses, return the first
                data = (data instanceof Array) ? data : [data];
                var newModels = [];
                data.map(function(d) { newModels.push(that.create(d)); });
                m = newModels ? newModels[0] : null;
            }
        });
    }
    return m;
});
el8_EntitySet.method('getDeletedById', function(id) {
    for (var i=0; i<this._deleted.length; i++) {
        if (this._deleted[i].getId() === id) {
            return this._deleted[i];
        }
    }
    return null;
});
el8_EntitySet.method('rebuild', function() {
    this._all = [];
    // Rebuild from mapped
    for (var currKey in this._mapped) {
        if (this._mapped.hasOwnProperty(currKey)) {
            this._all.push( this._mapped[currKey] );
        }
    }
});
el8_EntitySet.method('sorted', function() {
    if (this._sortFn) { this._all.sort(this._sortFn); }
    return this._all;
});
el8_EntitySet.method('getKey', function(item) {
    return item[this._getter]();
});
el8_EntitySet.method('getIdByKey', function(key) {
    var item = this.getByKey(key);
    if (item && item.getId) {
        return item.getId();
    }
    return null;
});
el8_EntitySet.method('getIdsByKey', function(keys) {
    return _.map(keys, this.getIdByKey.bind(this));
});
el8_EntitySet.method('remove', function(item, noNotify) {
    if (typeof(item) !== 'object') {
        // Item can be an ID; noFetch because we don't want
        // to request the item we're removing from the server...
        item = this.getById(item, true);
    }
    if (item) {
        this.removeFromMapped(item);
        this.rebuild();
        this.notifyObserver(noNotify);
        if (!noNotify) { this.trigger(EVENTS.DELETED, item); }
    }
    return item;
});
el8_EntitySet.method('model', function() {
    if (this._model) { return this._model; }
    else { return null; }
});
el8_EntitySet.method('unsaved', function() {
    return this._unsaved;
});
el8_EntitySet.method('deleted', function() {
    return this._deleted;
});
el8_EntitySet.method('saveItem', function(item, noNotify, opts) {
    var that = this;
    opts = opts || {};
    var moreOpts = {};
    if (opts.async !== undefined) { moreOpts.async = opts.async; }
    if (opts.oldItem !== undefined) { moreOpts.oldItem = opts.oldItem; }
    if (opts.isAutoSave !== undefined) { moreOpts.isAutoSave = opts.isAutoSave; }
    // Item can be an ID
    if (typeof(item) !== 'object') { item = that.getById(item); }
    if (that._saveFn) {
        var result = that._saveFn(
            item,
            function(data) {
                that.add(item, noNotify);
                if (opts.successCb) { opts.successCb(item, data); }
            },  /* successCallbackFn */
            function(data) {
                if (opts.errorCb) { opts.errorCb(item, data); }
            },  /* errorCallbackFn */
            moreOpts
        );
        return result;
    }
    return null;
});
el8_EntitySet.method('saveNew', function(spec, noNotify, opts) {
    var item = this.create(spec, true);
    return this.saveItem(item, noNotify, opts);
});
el8_EntitySet.method('deleteItem', function(item, noNotify, opts) {
    opts = opts || {};
    var that = this;
    // Item can be an ID
    if (typeof(item) !== 'object') { item = that.getById(item); }
    if (that._deleteFn) {
        var result = that._deleteFn(
            item,
            function() {
                that.remove(item, noNotify);
                if (opts.successCb) { opts.successCb(item); }
            },
            opts.errorCb,
            opts
        );
        return result;
    }
    return null;
});
el8_EntitySet.method('mergeItem', function(item, mergeItems, noNotify, opts) {
    opts = opts || {};
    var that = this;
    // Item can be an ID
    if (typeof(item) === 'object') { item = item.getId(); }
    if (that._mergeFn) {
        var result = that._mergeFn(
            item,
            mergeItems,
            function() {
                for (var i = 0; i < mergeItems.length; i++) {
                    that.remove(mergeItems[i], noNotify);
                    if (opts.successCb) { opts.successCb(item); }
                }

            },
            opts.errorCb,
            opts
        );
        return result;
    }
    return null;
});
el8_EntitySet.method('setSaveFn', function(fn) {
    if (this._saveFn) { UIM && UIM.logError('Uh-oh we are over-riding the save fn!'); }
    this._saveFn = fn;
});
el8_EntitySet.method('setDeleteFn', function(fn) {
    if (this._deleteFn) { UIM && UIM.logError('Uh-oh we are over-riding the delete fn!'); }
    this._deleteFn = fn;
});
el8_EntitySet.method('refreshById', function(id, opts) {
    var that = this;
    if (!id) { return null; }
    var urlFn = that._urlFn || that._urlIdFn;
    var onSuccess = opts && opts.onSuccess;
    var noNotify = opts && opts.noNotify;
    return urlFn ? (function() {
        var m = null;
        var fullOpts = Object.assign({
            url: jQuery.isFunction(urlFn) ? urlFn(id) : urlFn,
            async: false,
            success: function (data, textStatus, xhr) {
                if (data) {
                    m = that.create(data, noNotify);
                    if (onSuccess) {
                        onSuccess(m);
                    }
                } else {
                    that.remove(id, noNotify);
                    if (onSuccess) {
                        onSuccess(null);
                    }
                }
            }
        },opts)
        $.el8_ajax(fullOpts);
        return m;
    })() : that.getById(id, true);
});
el8_EntitySet.method('makeSetSortable', function(opts) {

    opts = opts || {};
    var rankKey = opts.rankKey || 'rank';
    var getRankFnName = 'get'+rankKey.capitalize();
    var sortFn = function(a, b) { return a[getRankFnName]() - b[getRankFnName](); };

    var that = this;
    this.reOrder = function(idList, successCb, errorCb, opts) {
        if (!that._urlSort) { return; }

        var data = {};
        data[rankKey] = idList;
        $.el8_ajax({
            type: 'POST',
            url: jQuery.isFunction(that._urlSort) ? that._urlSort() : that._urlSort,
            data: JSON.stringify(data),
            success: function(data) {
                that.create(data);
                if (successCb) { successCb(data); }
            },
            error: errorCb
        });
    };
    this._sortFn = sortFn;
});
el8_EntitySet.method('getModel', function() {
    return this._model;
});
